import { addDays, format } from "date-fns";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { APIs } from "../../apis";
import { OrderItemPlanningTab, type OrderItem } from "../../apis/order";
import type { DatePickerRangeValue } from "../../components";
import type { TableColumn } from "../../components/Table";
import { useApi, useNavigation, useSearchParams } from "../../hooks";
import { Constants } from "../../utils";
import { getManageBookingsColumns } from "./columns";

const DEFAULT_PAGE_SIZE = 10;

export function useViewModel() {
  const { t } = useTranslation();
  const { navigate } = useNavigation();
  const { fetch, data, loading } =
    useApi<Awaited<ReturnType<typeof APIs.order.orderItems>>>();

  const defaultDateRange = React.useMemo(() => {
    const today = new Date();
    return {
      from: format(today, "yyyy-MM-dd"),
      to: format(addDays(today, 30), "yyyy-MM-dd"),
    };
  }, []);

  const { searchParams, setSearchParams } = useSearchParams({
    searchString: "",
    planningTab: OrderItemPlanningTab.all,
    startDate: defaultDateRange.from,
    endDate: defaultDateRange.to,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const { results: dataSource = [], count: totalItems = 0 } =
    data?.orders ?? {};

  const skeletonOnNextLoad = React.useRef(true);

  const showSkeleton = loading && skeletonOnNextLoad.current;
  const showGlobalEmpty =
    !loading &&
    !totalItems &&
    searchParams.page === 1 &&
    searchParams.planningTab === OrderItemPlanningTab.all;

  const openBookingDetail = React.useCallback(
    (item: OrderItem) =>
      navigate(
        `${Constants.RoutePath.ManageBookings}/${item.orderId ?? item.order?.id}`,
      ),
    [navigate],
  );

  const columns = React.useMemo(() => getManageBookingsColumns({ t }), [t]);

  const getData = React.useCallback(async () => {
    try {
      await fetch(() =>
        APIs.order.orderItems({
          page: searchParams.page,
          pageSize: searchParams.pageSize,
          orderBy: "-serviceDateTime",
          planningTab: searchParams.planningTab,
          searchString: searchParams.searchString,
          startDate: searchParams.startDate,
          endDate: searchParams.endDate,
        }),
      );
    } catch {
    } finally {
      skeletonOnNextLoad.current = false;
    }
  }, [
    fetch,
    searchParams.startDate,
    searchParams.endDate,
    searchParams.page,
    searchParams.pageSize,
    searchParams.planningTab,
    searchParams.searchString,
  ]);

  React.useEffect(() => {
    void getData();
  }, [getData]);

  const setFilter = React.useCallback(
    (name: string, value: string | number | DatePickerRangeValue) => {
      if (name !== "planningTab") skeletonOnNextLoad.current = true;
      setSearchParams(
        name === "planningTab"
          ? { planningTab: value as string, page: 1 }
          : {
              ...(name === "dateRange" && typeof value === "object"
                ? { startDate: value.from, endDate: value.to }
                : { [name]: value }),
              planningTab: OrderItemPlanningTab.all,
              page: 1,
            },
      );
    },
    [setSearchParams],
  );

  const statusTabs = React.useMemo(
    () => [
      { label: t("general.all"), value: OrderItemPlanningTab.all },
      {
        label: t("general.upcoming"),
        value: OrderItemPlanningTab.upcoming,
      },
      {
        label: t("general.completed"),
        value: OrderItemPlanningTab.completed,
      },
      {
        label: t("general.cancelled"),
        value: OrderItemPlanningTab.cancelled,
      },
    ],
    [t],
  );

  return {
    t,
    columns: columns as TableColumn<OrderItem>[],
    dataSource,
    totalItems,
    loading,
    showSkeleton,
    showGlobalEmpty,
    page: searchParams.page,
    pageSize: searchParams.pageSize,
    searchString: searchParams.searchString,
    planningTab: searchParams.planningTab,
    dateRange: {
      from: searchParams.startDate,
      to: searchParams.endDate,
    },
    statusTabs,
    setFilter,
    onPageChange: (page: number) => setSearchParams({ page }),
    onPageSizeChange: (pageSize: number) =>
      setSearchParams({ pageSize, page: 1 }),
    onRowClick: openBookingDetail,
  };
}

export type ManageBookingsViewModel = ReturnType<typeof useViewModel>;
